// Pure helper for the "Referrals received by day" trend on My Patients.
// Counts loaded referral cases by referral_received_date (a Singapore calendar
// day). No I/O, no clock reads: today is always passed in.

// 'YYYY-MM-DD' calendar day, validated as a real date.
export type DateKey = string;

// Calendar month. `month` is 1-12.
export interface MonthKey {
  year: number;
  month: number;
}

export interface TrendCaseInput {
  id: unknown;
  referral_received_date: unknown;
}

// `selected` / `comparison` are counts, or null when the day is not observed:
// a day past the month's length, or a day after Singapore today.
export interface TrendDay {
  day: number;
  selected: number | null;
  comparison: number | null;
}

export interface ReferralTrend {
  days: TrendDay[];
  loadedCases: number;
  duplicateCases: number;
  invalidIdCases: number;
  excludedDateCases: number;
}

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_INPUT_RE = /^(\d{4})-(\d{2})$/;
export const MAX_DAYS = 31;

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const singaporeDateKey = (now: Date): DateKey => {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Asia/Singapore', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};

export const isDateKey = (value: unknown): value is DateKey => {
  if (typeof value !== 'string') return false;
  const match = DATE_KEY_RE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
};

export const monthKeyOfDate = (date: DateKey): MonthKey => ({
  year: Number(date.slice(0, 4)),
  month: Number(date.slice(5, 7)),
});

export const daysInMonth = (key: MonthKey): number =>
  new Date(Date.UTC(key.year, key.month, 0)).getUTCDate();

export const dateKeyFor = (key: MonthKey, day: number): DateKey =>
  `${key.year}-${pad2(key.month)}-${pad2(day)}`;

// Month input value ('YYYY-MM') for a month key.
export const monthInputValue = (key: MonthKey): string => `${key.year}-${pad2(key.month)}`;

// Parses a native month input value. Returns null for anything that is not a real month.
export const parseMonthInput = (value: unknown): MonthKey | null => {
  if (typeof value !== 'string') return null;
  const match = MONTH_INPUT_RE.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (year < 1900 || year > 2999 || month < 1 || month > 12) return null;
  return { year, month };
};

export const compareMonths = (a: MonthKey, b: MonthKey): number =>
  a.year !== b.year ? a.year - b.year : a.month - b.month;

export const previousMonth = (key: MonthKey): MonthKey =>
  key.month === 1 ? { year: key.year - 1, month: 12 } : { year: key.year, month: key.month - 1 };

// The default pair: the Singapore current month against the calendar month before it.
export const defaultMonths = (today: DateKey): { selected: MonthKey; comparison: MonthKey } => {
  const selected = monthKeyOfDate(today);
  return { selected, comparison: previousMonth(selected) };
};

// Null when the month may be charted; otherwise the message to show instead of a chart.
export const monthValidationMessage = (key: MonthKey | null, today: DateKey): string | null => {
  if (!key) return 'Pick a valid month.';
  if (compareMonths(key, monthKeyOfDate(today)) > 0) {
    return 'That month has not started yet. Choose this month or an earlier one.';
  }
  return null;
};

export const formatMonthLabel = (key: MonthKey): string =>
  new Intl.DateTimeFormat('en-SG', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(key.year, key.month - 1, 1)),
  );

const cleanId = (value: unknown): string | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const seriesValue = (
  key: MonthKey,
  day: number,
  today: DateKey,
  counts: Map<DateKey, number>,
): number | null => {
  if (day > daysInMonth(key)) return null;
  const date = dateKeyFor(key, day);
  if (date > today) return null;
  return counts.get(date) ?? 0;
};

// Counts each unique case ID once. Cases with a missing or invalid referral
// date are excluded from the chart and counted, never placed on a fallback day.
export const buildReferralTrend = (input: {
  cases: TrendCaseInput[];
  selected: MonthKey;
  comparison: MonthKey;
  today: DateKey;
}): ReferralTrend => {
  const { cases, selected, comparison, today } = input;
  const seen = new Set<string>();
  const counts = new Map<DateKey, number>();
  let loadedCases = 0;
  let duplicateCases = 0;
  let invalidIdCases = 0;
  let excludedDateCases = 0;

  for (const item of cases) {
    const id = cleanId(item.id);
    if (id === null) {
      invalidIdCases++;
      continue;
    }
    if (seen.has(id)) {
      duplicateCases++;
      continue;
    }
    seen.add(id);
    loadedCases++;
    const date = item.referral_received_date;
    if (!isDateKey(date)) {
      excludedDateCases++;
      continue;
    }
    counts.set(date, (counts.get(date) ?? 0) + 1);
  }

  const days: TrendDay[] = [];
  for (let day = 1; day <= MAX_DAYS; day++) {
    days.push({
      day,
      selected: seriesValue(selected, day, today, counts),
      comparison: seriesValue(comparison, day, today, counts),
    });
  }

  return { days, loadedCases, duplicateCases, invalidIdCases, excludedDateCases };
};
